import type { RootState } from "@react-three/fiber";

// html-to-image copies a canvas through its toDataURL, called asynchronously during the capture: by
// then the browser has cleared the drawing buffer of a WebGL canvas, so the 3D views came out blank.
// preserveDrawingBuffer would keep it, at the cost of a buffer copy on every frame: instead, the
// registered canvases are drawn again right before the capture and read in the same task, while the
// buffer still holds the frame.
const canvasStates = new WeakMap<HTMLCanvasElement, RootState["get"]>();

/** Makes a react-three-fiber canvas capturable by the screenshot, to call from its Canvas onCreated. */
export const registerCapturableCanvas = ({ gl, get }: RootState) => {
  canvasStates.set(gl.domElement, get);
};

/** Snapshots the registered WebGL canvases below root and returns the function restoring them. */
export const snapshotWebGLCanvases = (root: HTMLElement): (() => void) => {
  const snapshotCanvases = Array.from(root.querySelectorAll("canvas")).filter((canvas) => {
    const getState = canvasStates.get(canvas);

    if (!getState) {
      return false;
    }

    try {
      const { gl, scene, camera } = getState();
      gl.render(scene, camera);
      const dataUrl = canvas.toDataURL();
      // html-to-image gets the frame read above, instead of the cleared buffer
      canvas.toDataURL = () => dataUrl;
      return true;
    } catch {
      // e.g. a lost WebGL context: the canvas comes out blank
      return false;
    }
  });

  return () => {
    snapshotCanvases.forEach((canvas) => Reflect.deleteProperty(canvas, "toDataURL"));
  };
};
